import { useMemo, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Activity,
  ArrowDown,
  ArrowUp,
  Edit,
  GitMerge,
  GripVertical,
  Plus,
  Tag,
  Trash2,
  X,
} from 'lucide-react';
import type { TagAdminItem } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toaster';
import { TagEditDialog } from '@/components/taxonomies/tag-edit-dialog';
import { useSession } from '@/lib/auth';
import { hasPermission } from '@/lib/permissions';
import {
  tagsListQueryOptions,
  useDeleteTag,
  useMergeTags,
  useReorderTags,
  useUpdateTag,
} from '@/lib/taxonomies';

interface TagsSearch {
  q?: string;
  page?: number;
}

export const Route = createFileRoute('/_authenticated/tags/')({
  validateSearch: (search: Record<string, unknown>): TagsSearch => ({
    q: typeof search.q === 'string' ? search.q : undefined,
    page: typeof search.page === 'number' && search.page > 1 ? search.page : undefined,
  }),
  component: TagsPage,
});

function TagsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useSession();
  const currentPage = search.page ?? 1;
  const query = useQuery(tagsListQueryOptions(search.q, currentPage));
  const deleteMutation = useDeleteTag();
  const updateMutation = useUpdateTag();
  const mergeMutation = useMergeTags();
  const reorderMutation = useReorderTags();

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<TagAdminItem | undefined>(undefined);
  const [pendingDelete, setPendingDelete] = useState<TagAdminItem | null>(null);
  const [searchInput, setSearchInput] = useState(search.q ?? '');
  // Inline-rename state: row being edited + the in-flight draft.
  // Single-row-at-a-time intentionally — multi-edit would race the
  // shared mutation and confuse the loading indicator.
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  // Merge state — selected source tags + the picked target.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState('');

  // 5px activation distance so clicking row buttons (rename / merge
  // checkbox / action icons) doesn't accidentally start a drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const commitRename = (tag: TagAdminItem) => {
    const next = renameDraft.trim();
    setRenameId(null);
    // No-op on blank or unchanged input — avoid empty-name 400s + audit
    // noise from "rename to same value".
    if (!next || next === tag.name) return;
    updateMutation.mutate({ id: tag.id, input: { name: next } });
  };

  const canManage = hasPermission(user, 'tag.manage');
  const canReadAudit = hasPermission(user, 'audit.read');
  const items = query.data?.items ?? [];
  const pagination = query.data?.pagination;
  const total = pagination?.total ?? items.length;
  const perPage = pagination?.perPage ?? 50;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const orderedIds = useMemo(() => items.map((t) => t.id), [items]);

  // Reorder is admin curation over the FULL tag set. Disable both drag
  // and ↑/↓ when a search filter is active OR when the list spans more
  // than one page — renumbering only the visible slice would collide with
  // sort_order on other pages and scramble the global ordering. With the
  // feed-bloated tag count this effectively stays off until consolidation
  // brings tags back under one page, at which point it re-enables itself.
  const isFiltered = Boolean(search.q);
  const canReorder = canManage && !isFiltered && total <= perPage;

  const goPage = (page: number) => {
    void navigate({ search: { ...search, page: page > 1 ? page : undefined } });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void navigate({ search: { q: searchInput.trim() || undefined, page: undefined } });
  };

  const openCreate = () => {
    setEditing(undefined);
    setEditOpen(true);
  };
  const openEdit = (t: TagAdminItem) => {
    setEditing(t);
    setEditOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, {
      onSuccess: () => setPendingDelete(null),
      onError: () => setPendingDelete(null),
    });
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const openMergeDialog = () => {
    setMergeTargetId('');
    setMergeOpen(true);
  };
  const handleConfirmMerge = () => {
    if (!mergeTargetId || selected.size === 0) return;
    mergeMutation.mutate(
      { fromIds: Array.from(selected), toId: mergeTargetId },
      {
        onSuccess: () => {
          clearSelection();
          setMergeOpen(false);
        },
      },
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = orderedIds.indexOf(String(active.id));
    const to = orderedIds.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    reorderMutation.mutate(arrayMove(orderedIds, from, to));
  };

  // ↑/↓ button click — adjacent swap. Compute the new order
  // client-side then post the full sequence; same endpoint as drag.
  const moveOne = (id: string, direction: 'up' | 'down') => {
    const from = orderedIds.indexOf(id);
    if (from === -1) return;
    const to = direction === 'up' ? from - 1 : from + 1;
    if (to < 0 || to >= orderedIds.length) return; // at edge — silent
    reorderMutation.mutate(arrayMove(orderedIds, from, to));
  };

  // Target candidates = every tag NOT currently selected as a source.
  // The schema also rejects toId ∈ fromIds, so this prevents a wasted
  // round-trip more than it adds safety.
  const mergeTargetOptions = items.filter((t) => !selected.has(t.id));

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              แท็ก
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {total > 0 ? `${total.toLocaleString()} แท็ก` : 'ยังไม่มีแท็ก'}
            </p>
          </div>
          {canManage && (
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              สร้างแท็ก
            </Button>
          )}
        </div>

        {/* Server-side filter — backend tagsListQuerySchema already
            accepts q. Helps when the list grows past one screen
            (typical after a few months of usage). */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="ค้นหาชื่อหรือ slug..."
            className="max-w-md"
          />
          <Button type="submit" variant="outline">
            ค้นหา
          </Button>
          {search.q && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSearchInput('');
                void navigate({ search: {} });
              }}
            >
              ล้าง
            </Button>
          )}
        </form>

        {isFiltered && canManage && (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            ปิดการเรียงลำดับขณะค้นหา — ล้างคำค้นก่อนถึงจะลาก / กด ↑↓ ได้
          </p>
        )}

        {query.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
            กำลังโหลด...
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white p-12 text-center dark:border-slate-800 dark:bg-slate-900">
            <Tag className="h-12 w-12 text-slate-300" />
            <p className="text-sm text-slate-500">
              {search.q ? `ไม่พบแท็กที่ตรงกับ "${search.q}"` : 'ยังไม่มีแท็ก'}
            </p>
            {!search.q && canManage && (
              <Button size="sm" onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                สร้างแท็กแรก
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
            {/* Bulk merge bar — appears only when canManage AND at least
                one source is selected. The actual target picker lives in
                a dialog so the toolbar stays compact for the common
                non-merge browsing case. */}
            {canManage && selected.size > 0 && (
              <div className="bg-brand-50 dark:bg-brand-900/30 flex flex-wrap items-center gap-3 border-b border-slate-200 p-3 dark:border-slate-800">
                <span className="text-sm font-medium">เลือก {selected.size} แท็ก</span>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={openMergeDialog}
                  disabled={mergeMutation.isPending}
                >
                  <GitMerge className="mr-1.5 h-3.5 w-3.5" />
                  รวมเข้าแท็กอื่น...
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  className="ml-auto"
                >
                  <X className="mr-1.5 h-3.5 w-3.5" />
                  ล้างเลือก
                </Button>
              </div>
            )}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
                    <tr>
                      {canReorder && <th className="w-8 px-2 py-3 font-medium" />}
                      {canManage && <th className="w-10 px-4 py-3 font-medium" />}
                      <th className="px-4 py-3 font-medium">ชื่อ</th>
                      <th className="px-4 py-3 font-medium">Slug</th>
                      <th className="px-4 py-3 font-medium">บทความ</th>
                      <th className="px-4 py-3 font-medium">แก้ไขล่าสุด</th>
                      {(canManage || canReadAudit) && <th className="w-44 px-4 py-3 font-medium" />}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {items.map((t, i) => (
                      <TagRow
                        key={t.id}
                        tag={t}
                        index={i}
                        total={items.length}
                        canManage={canManage}
                        canReorder={canReorder}
                        canReadAudit={canReadAudit}
                        selected={selected.has(t.id)}
                        onToggleSelect={() => toggleSelect(t.id)}
                        renameActive={renameId === t.id}
                        renameDraft={renameDraft}
                        onRenameStart={() => {
                          setRenameId(t.id);
                          setRenameDraft(t.name);
                        }}
                        onRenameChange={setRenameDraft}
                        onRenameCommit={() => commitRename(t)}
                        onRenameCancel={() => setRenameId(null)}
                        renameDisabled={updateMutation.isPending}
                        onMoveUp={() => moveOne(t.id, 'up')}
                        onMoveDown={() => moveOne(t.id, 'down')}
                        reorderPending={reorderMutation.isPending}
                        onEdit={() => openEdit(t)}
                        onDelete={() => setPendingDelete(t)}
                        deleteDisabled={deleteMutation.isPending}
                      />
                    ))}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          </div>
        )}

        {/* Pager — tags run into the thousands, so without this most of
            the list is unreachable except by search. Hidden on single-page
            results to keep the small-tag-set view clean. */}
        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <span className="text-xs text-slate-500">
              แสดง {(currentPage - 1) * perPage + 1}–{Math.min(currentPage * perPage, total)} จาก{' '}
              {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => goPage(currentPage - 1)}
              >
                ก่อนหน้า
              </Button>
              <span className="text-slate-500">
                หน้า {currentPage} จาก {totalPages.toLocaleString()}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => goPage(currentPage + 1)}
              >
                ถัดไป
              </Button>
            </div>
          </div>
        )}
      </div>

      <TagEditDialog open={editOpen} onOpenChange={setEditOpen} editing={editing} />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="ยืนยันการลบแท็ก"
        description={
          pendingDelete
            ? `จะลบแท็ก "${pendingDelete.name}" (มี ${pendingDelete.postCount} บทความผูกอยู่ — จะถูกถอดออกจากแท็กนี้ แต่บทความจะยังอยู่)`
            : undefined
        }
        confirmLabel="ลบ"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={deleteMutation.isPending}
      />

      <Dialog
        open={mergeOpen}
        onOpenChange={(open) => {
          if (!open) setMergeOpen(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-slate-400" />
              รวม {selected.size} แท็กเข้าแท็กเดียว
            </DialogTitle>
            <DialogDescription>
              ทุก post_tags ref ของแท็กต้นทางจะถูกย้ายไปยังแท็กปลายทาง แล้วลบแท็กต้นทาง.
              บทความที่ผูกทั้ง 2 ฝั่งอยู่แล้วจะไม่ถูกซ้ำ.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs dark:border-slate-700 dark:bg-slate-800/50">
              <div className="mb-1 font-medium text-slate-600 dark:text-slate-300">
                ต้นทาง (จะถูกลบ):
              </div>
              <div className="flex flex-wrap gap-1">
                {Array.from(selected).map((id) => {
                  const tag = items.find((t) => t.id === id);
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 font-mono text-[11px] text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    >
                      {tag?.name ?? id.slice(0, 8)}
                    </span>
                  );
                })}
              </div>
            </div>

            <div>
              <label
                htmlFor="merge-target"
                className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200"
              >
                ปลายทาง
              </label>
              <select
                id="merge-target"
                value={mergeTargetId}
                onChange={(e) => setMergeTargetId(e.target.value)}
                className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <option value="">-- เลือกแท็กปลายทาง --</option>
                {mergeTargetOptions.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.postCount} บทความ)
                  </option>
                ))}
              </select>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setMergeOpen(false)}
              disabled={mergeMutation.isPending}
            >
              ยกเลิก
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={handleConfirmMerge}
              disabled={mergeMutation.isPending || !mergeTargetId}
            >
              {mergeMutation.isPending ? 'กำลังรวม...' : `รวม ${selected.size} แท็ก`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface TagRowProps {
  tag: TagAdminItem;
  index: number;
  total: number;
  canManage: boolean;
  canReorder: boolean;
  canReadAudit: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  renameActive: boolean;
  renameDraft: string;
  onRenameStart: () => void;
  onRenameChange: (v: string) => void;
  onRenameCommit: () => void;
  onRenameCancel: () => void;
  renameDisabled: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  reorderPending: boolean;
  onEdit: () => void;
  onDelete: () => void;
  deleteDisabled: boolean;
}

function TagRow({
  tag: t,
  index,
  total,
  canManage,
  canReorder,
  canReadAudit,
  selected,
  onToggleSelect,
  renameActive,
  renameDraft,
  onRenameStart,
  onRenameChange,
  onRenameCommit,
  onRenameCancel,
  renameDisabled,
  onMoveUp,
  onMoveDown,
  reorderPending,
  onEdit,
  onDelete,
  deleteDisabled,
}: TagRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: t.id,
    disabled: !canReorder,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const isFirst = index === 0;
  const isLast = index === total - 1;

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={
        isDragging
          ? 'bg-brand-50/60 dark:bg-brand-900/30 relative z-10 shadow-md'
          : selected
            ? 'bg-brand-50/50 dark:bg-brand-900/20'
            : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
      }
    >
      {canReorder && (
        <td className="px-2 py-3 align-middle">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="cursor-grab text-slate-400 hover:text-slate-600 active:cursor-grabbing dark:hover:text-slate-200"
            aria-label="ลากเรียงลำดับ"
            title="ลากเพื่อเรียงลำดับ"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </td>
      )}
      {canManage && (
        <td className="px-4 py-3">
          <input
            type="checkbox"
            aria-label={`เลือก ${t.name}`}
            checked={selected}
            onChange={onToggleSelect}
            className="h-4 w-4"
          />
        </td>
      )}
      <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-50">
        {/* Inline rename — click the name to edit, Enter or blur saves,
            Escape cancels. Skip when caller lacks tag.manage so
            non-managers see plain text (backend would 403 anyway). */}
        {canManage && renameActive ? (
          <Input
            autoFocus
            value={renameDraft}
            onChange={(e) => onRenameChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onRenameCommit();
              else if (e.key === 'Escape') onRenameCancel();
            }}
            onBlur={onRenameCommit}
            disabled={renameDisabled}
            className="h-7 max-w-xs px-2 py-0 text-sm"
          />
        ) : canManage ? (
          <button
            type="button"
            onClick={onRenameStart}
            className="-mx-1 rounded px-1 text-left hover:bg-slate-100 dark:hover:bg-slate-800"
            title="คลิกเพื่อแก้ชื่อ"
          >
            {t.name}
          </button>
        ) : (
          t.name
        )}
      </td>
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard
              .writeText(`/${t.slug}`)
              .then(() => toast.success(`คัดลอก /${t.slug}`))
              .catch(() => {
                // Clipboard denied (non-secure context) — silent.
              });
          }}
          className="hover:text-brand-600 dark:hover:text-brand-400 font-mono text-xs text-slate-500"
          title="คลิกเพื่อคัดลอก"
        >
          /{t.slug}
        </button>
      </td>
      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
        {t.postCount > 0 ? (
          <Link
            to="/posts"
            search={{ tagId: t.id }}
            className="hover:text-brand-600 dark:hover:text-brand-400 underline-offset-2 hover:underline"
            title={`ดูบทความที่แท็ก ${t.name}`}
          >
            {t.postCount}
          </Link>
        ) : (
          <span className="text-slate-400">0</span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-slate-500">
        {new Date(t.updatedAt).toLocaleDateString('th-TH')}
        {Date.now() - new Date(t.createdAt).getTime() < 24 * 60 * 60 * 1000 && (
          <span className="ml-1 inline-flex items-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
            ใหม่
          </span>
        )}
      </td>
      {(canManage || canReadAudit) && (
        <td className="px-4 py-3 text-right">
          <div className="flex justify-end gap-1">
            {canReorder && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onMoveUp}
                  disabled={reorderPending || isFirst}
                  aria-label="เลื่อนขึ้น"
                  title="เลื่อนขึ้น 1 ตำแหน่ง"
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onMoveDown}
                  disabled={reorderPending || isLast}
                  aria-label="เลื่อนลง"
                  title="เลื่อนลง 1 ตำแหน่ง"
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
              </>
            )}
            {canReadAudit && (
              <Link
                to="/audit"
                search={{ resourceType: 'tag', resourceId: t.id }}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                title="ดู audit ของแท็กนี้"
                aria-label="ดู audit"
              >
                <Activity className="h-4 w-4" />
              </Link>
            )}
            {canManage && (
              <Button variant="ghost" size="icon" onClick={onEdit} aria-label="แก้ไข">
                <Edit className="h-4 w-4" />
              </Button>
            )}
            {canManage && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onDelete}
                disabled={deleteDisabled}
                aria-label="ลบ"
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}
